import React from 'react';

import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import { cn } from '@/lib/utils';

export interface SavePipelinePopupProps {
  open: boolean;
  name: string;
  description: string;
  error: string | null;
  canSave: boolean;
  includeInputData: boolean;
  inputDataSize: number;
  mode: 'create' | 'update';
  onChangeName: (value: string) => void;
  onChangeDescription: (value: string) => void;
  onChangeIncludeInputData: (value: boolean) => void;
  onClose: () => void;
  onSave: () => void;
  onSaveAsNew: () => void;
}

function formatBytes(bytes: number): string {
  if (bytes === 0) return '0 B';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

export const SavePipelinePopup: React.FC<SavePipelinePopupProps> = ({
  open,
  name,
  description,
  error,
  canSave,
  includeInputData,
  inputDataSize,
  mode,
  onChangeName,
  onChangeDescription,
  onChangeIncludeInputData,
  onClose,
  onSave,
  onSaveAsNew,
}) => {
  if (!open) return null;

  const isUpdate = mode === 'update';

  return (
    <div className="absolute inset-0 pointer-events-none">
      <div className="h-full w-full flex justify-end">
        <div className="pointer-events-auto h-full w-full max-w-xl border-l border-border bg-background">
          <div className="h-14 border-b border-border flex items-center justify-between px-6">
            <div className="text-lg font-semibold">
              {isUpdate ? 'Update pipeline' : 'Save pipeline'}
            </div>
            <Button variant="ghost" size="sm" onClick={onClose} aria-label="Close">
              ✕
            </Button>
          </div>

          <div className="p-6 overflow-auto h-[calc(100%-3.5rem)]">
            <div className="space-y-4">
              <div className="space-y-1">
                <label className="text-xs font-medium text-muted-foreground">Name</label>
                <Input
                  value={name}
                  onChange={(e) => onChangeName(e.target.value)}
                  placeholder="My pipeline"
                />
              </div>

              <div className="space-y-1">
                <label className="text-xs font-medium text-muted-foreground">Description</label>
                <textarea
                  className="w-full min-h-28 p-3 bg-background border border-border rounded-md resize-none text-sm focus:outline-none"
                  value={description}
                  onChange={(e) => onChangeDescription(e.target.value)}
                  placeholder="What does this pipeline do?"
                />
              </div>

              <div className="space-y-2">
                <div className="flex items-center space-x-2">
                  <Checkbox
                    id="include-input"
                    checked={includeInputData}
                    onCheckedChange={onChangeIncludeInputData}
                  />
                  <label
                    htmlFor="include-input"
                    className="text-sm font-medium cursor-pointer select-none"
                  >
                    Include current input data
                  </label>
                </div>
                {includeInputData && inputDataSize > 0 && (
                  <div className={cn("text-xs text-muted-foreground pl-6")}>
                    Input: {formatBytes(inputDataSize)}
                  </div>
                )}
              </div>

              {error ? <div className="text-sm text-destructive">{error}</div> : null}

              <div className="flex items-center justify-between pt-2">
                <div>
                  {isUpdate && (
                    <Button variant="ghost" size="sm" onClick={onSaveAsNew}>
                      Save as new copy
                    </Button>
                  )}
                </div>
                <div className="flex items-center gap-2">
                  <Button variant="ghost" size="sm" onClick={onClose}>
                    Cancel
                  </Button>
                  <Button
                    variant="secondary"
                    size="sm"
                    onClick={onSave}
                    disabled={!canSave}
                  >
                    {isUpdate ? 'Update' : 'Save'}
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
